/* eslint-disable react/prop-types */ // TODO: add props
import { Fragment } from 'react';
import PT from 'prop-types';
import { Link } from 'react-router-dom';
import Immutable from 'immutable';
import {
  CORRECTABLE_EXAM_QUESTION_TYPES,
  EXAM_QUESTION_TYPES, // not used for ExamGrid
} from 'edrolo/constants/QuestionTypes';
import { ProgressBar } from 'edrolo/domains/studentProgressGrid';
import { Table } from 'edrolo/domains/teacherResultsTable';
import ReflectionCell from './ReflectionCell';
import urlResolver from 'edrolo/utils/URLResolver';
import { EXAM_ANALYTICS_CONSTANTS } from 'edrolo/domains/exams';
import style from './QuestionTableBody.scss';
import { Icon } from 'edrolo-style-guide';
import cx from 'classnames';

const StudentNameLink = ({
  profileId,
  userId,
  classId,
  children,
  navigatedFrom,
}) => {
  const toUrl = urlResolver.reverse('student-course-progress', {
    classId,
    profileId,
  });
  const to = {
    pathname: toUrl,
    state: { navigatedFrom },
  };

  return (
    <Link
      to={to}
      className={style.studentNameLink}
      onClick={() => {
        global.analytics.track(
          EXAM_ANALYTICS_CONSTANTS.EVENTS
            .CLICKED_LINK_TO_STUDENT_COURSE_PROGRESS,
          {
            origin: EXAM_ANALYTICS_CONSTANTS.ORIGIN.EXAMS,
            page: EXAM_ANALYTICS_CONSTANTS.PAGES.QUESTION_SET_RESULTS_GRID,
            class_id: classId,
            student_user_id: userId,
          }
        );
      }}
    >
      {children}
    </Link>
  );
};

const StudentRow = ({
  studentName,
  profileId,
  userId,
  classId,
  totalUnderstands,
  examSitting,
  getQuestionReviewUrl,
  flatQuestions,
  indicesToAddSpacerCellTo,
  showCorrectLayer,
  showIncorrectLayer,
  showName,
  showUnderstood,
  navigatedFrom,
  analyticsPayload,
  shouldHighlightRow = false,
}) => {
  const { SINGLE_ANSWER_MULTI_CHOICE, SELECT_MULTI } = EXAM_QUESTION_TYPES;

  // build a list of student to question responses,
  // inserting a Map() with just the 'question' key where there's no response
  const studentQuestionResponses = flatQuestions
    .toList()
    .map((question) =>
      examSitting
        .get('responses', Immutable.List())
        .find(
          (response) => response.get('question') === question.get('id'),
          null,
          Immutable.Map({ question: question.get('id') })
        )
    );

  return (
    <Table.Row className={style.bodyRow}>
      {showName ? (
        <Table.RowHeader
          isSticky
          className={cx(style.studentNameRowHeader, {
            [style.tableRowHighlighted]: shouldHighlightRow,
          })}
        >
          <StudentNameLink
            profileId={profileId}
            userId={userId}
            classId={classId}
            navigatedFrom={navigatedFrom}
          >
            <span data-mp-mask>{studentName}</span>
          </StudentNameLink>
        </Table.RowHeader>
      ) : null}
      {showUnderstood ? (
        <Table.Cell
          className={cx(style.progressCell, {
            [style.tableRowHighlighted]: shouldHighlightRow,
          })}
        >
          <div className={style.progressBar}>
            <ProgressBar
              value={totalUnderstands}
              maxValue={flatQuestions.size}
              indicatorClass={style.progressIndicator}
              condensed
            />
          </div>
        </Table.Cell>
      ) : null}

      {studentQuestionResponses.map((questionResponse, idx) => {
        const questionId = questionResponse.get('question');
        const question = flatQuestions.get(`${questionId}`);
        const hasResponse = !!questionResponse.get('id');
        const questionType = question.get('question_type');

        const getStudentAnswer = () => {
          if (questionType === SINGLE_ANSWER_MULTI_CHOICE) {
            return questionResponse.get('your_answer');
          }
          if (questionType === SELECT_MULTI && hasResponse) {
            return <Icon name="overflow" />;
          }
          return '';
        };
        const studentAnswer = getStudentAnswer();

        const isQuestionCorrectable = CORRECTABLE_EXAM_QUESTION_TYPES.includes(
          question.get('question_type')
        );
        const isCorrect =
          isQuestionCorrectable &&
          hasResponse &&
          questionResponse.get('is_correct', false);
        const isIncorrect = isQuestionCorrectable && hasResponse && !isCorrect;

        return (
          <Fragment key={questionResponse.get('question')}>
            <Table.Cell
              className={cx({
                [style.tableRowHighlighted]: shouldHighlightRow,
              })}
            >
              <ReflectionCell
                grokked={questionResponse.get('grokked')}
                to={{
                  pathname: getQuestionReviewUrl(
                    questionResponse.get('question')
                  ),
                  state: {
                    highlightProfileId: profileId,
                  },
                }}
                onClick={(e) => {
                  global.analytics.track(
                    EXAM_ANALYTICS_CONSTANTS.EVENTS
                      .CLICKED_QUESTION_GRID_STUDENT_RESPONSE,
                    {
                      origin: analyticsPayload.origin,
                      page: analyticsPayload.page,
                      class_id: analyticsPayload.classId,
                      lesson_id: analyticsPayload.lessonId,
                      exam_id: analyticsPayload.examId,
                      question_id: questionId,
                    }
                  );
                }}
                studentAnswer={studentAnswer}
                isCorrect={isCorrect && showCorrectLayer}
                isIncorrect={isIncorrect && showIncorrectLayer}
              />
            </Table.Cell>
            {indicesToAddSpacerCellTo?.includes(idx) ? (
              <Table.Cell spacer />
            ) : null}
          </Fragment>
        );
      })}
    </Table.Row>
  );
};

StudentRow.propTypes = {
  profileId: PT.number,
};

export default StudentRow;
